<template>
  <div ref="anchor" class="discountAnchor" :class="{fullWidth}">
    <DiscountBanner :auto-open-on-mobile="autoOpenOnMobile"/>
    <transition name="discountSlide" :css="!suppressFloating">
      <div v-if="showFloating && !suppressFloating" ref="floating" class="floatingDiscount" :class="{mobile}" :style="floatingStyle">
        <DiscountBanner :compact="mobile" :tile="mobile"/>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import BasicComponent from '@/global/BasicComponent';
import DiscountBanner from '@/components/DiscountBanner.vue';
import {Prop} from 'vue-property-decorator';

@Component({components: {DiscountBanner}})
export default class StickyDiscountBanner extends BasicComponent {
  @Prop({type: Function, required: true}) getBoundary: () => HTMLElement | null;
  @Prop({type: Boolean, default: false}) fullWidth: boolean;
  @Prop({type: Boolean, default: true}) autoOpenOnMobile: boolean;
  @Prop({type: Boolean, default: false}) suppressFloating: boolean;
  showFloating = false;
  left = 0;
  top = 0;
  width = 0;
  resizeObserver: ResizeObserver | null = null;
  positionFrame: number | null = null;


  get floatingStyle() {
    return this.mobile ? {top: `${this.top}px`} : {
      left: `${this.left}px`, top: `${this.top}px`, width: `${this.width}px`,
    };
  }

  mounted() {
    const anchor = this.$refs.anchor as HTMLElement;
    // The floating copy follows the view boundary even inside clipped stepper panels.
    this.resizeObserver = new ResizeObserver(this.schedulePositionUpdate);
    this.resizeObserver.observe(anchor);
    this.$nextTick(() => {
      const products = this.getBoundary();
      if (products) {
        this.resizeObserver?.observe(products);
      }
      this.schedulePositionUpdate();
    });
    window.addEventListener('scroll', this.schedulePositionUpdate, {passive: true});
    window.addEventListener('resize', this.schedulePositionUpdate);
    this.updatePosition();
  }

  schedulePositionUpdate() {
    if (this.positionFrame !== null) {
      return;
    }
    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null;
      this.updatePosition();
    });
  }

  updatePosition() {
    const anchor = this.$refs.anchor as HTMLElement;
    const rect = anchor.getBoundingClientRect();
    // Inactive stepper panels have no layout and must not show a floating copy.
    const productsBottom = this.getBoundary()?.getBoundingClientRect().bottom ?? 0;
    this.showFloating = rect.width > 0 && rect.bottom <= 0 && productsBottom > 0;
    this.left = rect.left;
    this.width = rect.width;
    const height = (this.$refs.floating as HTMLElement | undefined)?.getBoundingClientRect().height ?? rect.height;
    const style = window.getComputedStyle(anchor);
    const topGap = parseFloat(style.getPropertyValue('--discount-floating-top-gap')) || 0;
    this.top = Math.min(topGap, productsBottom - height);
  }

  beforeDestroy() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.schedulePositionUpdate);
    window.removeEventListener('resize', this.schedulePositionUpdate);
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
    }
  }
}
</script>

<style scoped lang="scss">
.discountAnchor {
  --discount-floating-top-gap: 12px;

  @media (max-width: 959px) {
    --discount-floating-top-gap: 0px;
  }
  width: 100%;
  ::v-deep .discountBanner {
    width: 100%;
  }
  @media (min-width: 960px) {
    width: 85%;
    max-width: 640px;
    margin-left: auto;
  }
  &.fullWidth {
    width: 100%;
    max-width: none;
  }
}
// Raise the whole stacking context so its coupon details can cover the basket dock.
.floatingDiscount {
  position: fixed;
  z-index: 130;
}
.floatingDiscount.mobile {
  left: 0;
  right: 0;
}
.discountSlide-enter-active, .discountSlide-leave-active {
  transition: transform .2s ease, opacity .2s ease;
}
.discountSlide-enter, .discountSlide-leave-to {
  transform: translateY(calc(-100% - 12px));
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .discountSlide-enter-active, .discountSlide-leave-active {
    transition: none;
  }
}
</style>
